/**
 * Phase 9.1 — Follow-up draft modal.
 *
 * Opens from the lead detail drawer (and directly from a pipeline card icon).
 * On open: optionally pre-generates a draft. User edits inline, picks tone,
 * can regenerate, then either sends (via /api/leads/{id}/send-followup) or
 * saves as activity-log-only.
 *
 * Compliance flags returned by the API are shown as a yellow warning banner
 * above the body textarea — the LLM did its own auto-rewrite loop server-side,
 * so these flags are what REMAINS unfixable (should be rare).
 */
import React, { useEffect, useState } from 'react'
import { X, Loader2, Sparkles, Send, Save, RefreshCw, AlertTriangle } from 'lucide-react'

const API_URL = process.env.REACT_APP_BACKEND_URL || ''

export default function FollowupDraftModal({ leadId, leadName, adminKey, onClose }) {
  const [tone, setTone] = useState('warm')
  const [draft, setDraft] = useState(null) // { subject, body_markdown, compliance_flags, model }
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [state, setState] = useState('idle') // idle | drafting | sending | saving | sent | saved | error
  const [error, setError] = useState('')

  const generate = async (t = tone) => {
    setState('drafting')
    setError('')
    try {
      const res = await fetch(
        `${API_URL}/api/leads/${encodeURIComponent(leadId)}/draft-followup?admin_key=${encodeURIComponent(adminKey)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tone: t }),
        }
      )
      if (res.status === 503) {
        setState('error')
        setError('AI features are disabled on this deployment.')
        return
      }
      const data = await res.json()
      if (!data.ok) {
        setState('error')
        setError(data.error || 'Draft generation failed. Try again.')
        return
      }
      setDraft(data)
      setSubject(data.subject || '')
      setBody(data.body_markdown || '')
      setState('idle')
    } catch (e) {
      setState('error')
      setError('Network error — try again.')
    }
  }

  useEffect(() => {
    if (leadId && adminKey) generate(tone)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId])

  const submit = async (saveOnly) => {
    setState(saveOnly ? 'saving' : 'sending')
    setError('')
    try {
      const res = await fetch(
        `${API_URL}/api/leads/${encodeURIComponent(leadId)}/send-followup?admin_key=${encodeURIComponent(adminKey)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subject, body_markdown: body, save_only: saveOnly }),
        }
      )
      const data = await res.json()
      if (!res.ok || !data.ok) {
        setState('error')
        setError(data.detail || 'Send failed.')
        return
      }
      setState(saveOnly ? 'saved' : data.sent ? 'sent' : 'saved')
    } catch (e) {
      setState('error')
      setError('Network error — try again.')
    }
  }

  const flags = draft?.compliance_flags || []
  const isBusy = state === 'drafting' || state === 'sending' || state === 'saving'

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="followup-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(11,37,69,0.55)', backdropFilter: 'blur(4px)' }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 680,
          maxHeight: '90vh',
          overflowY: 'auto',
          background: 'var(--admin-surface)',
          borderRadius: 14,
          color: 'var(--admin-slate)',
          border: '1px solid var(--admin-gold)',
          padding: 24,
          fontFamily: 'Inter,sans-serif',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--admin-gold)' }}>
              <Sparkles size={13} /> AI draft
            </div>
            <h2 style={{ fontFamily: 'Fraunces,serif', margin: '6px 0 0 0', color: 'var(--admin-navy)', fontSize: 24 }}>
              Follow-up to {leadName || leadId}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="followup-close"
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', color: 'var(--admin-muted)', cursor: 'pointer' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Tone selector */}
        <div style={{ display: 'flex', gap: 6, margin: '12px 0' }}>
          {['warm', 'professional', 'urgent'].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setTone(t)
                generate(t)
              }}
              data-testid={`followup-tone-${t}`}
              disabled={isBusy}
              style={{
                padding: '6px 12px',
                borderRadius: 20,
                border: `1px solid ${tone === t ? 'var(--admin-gold-solid)' : 'var(--admin-border)'}`,
                background: tone === t ? 'var(--admin-gold-solid)' : 'transparent',
                color: tone === t ? '#FFFFFF' : 'var(--admin-slate)',
                fontSize: 12,
                fontWeight: 500,
                cursor: isBusy ? 'wait' : 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {t}
            </button>
          ))}
          <button
            type="button"
            onClick={() => generate(tone)}
            disabled={isBusy}
            data-testid="followup-regenerate"
            title="Regenerate"
            style={{
              marginLeft: 'auto',
              padding: '6px 10px',
              background: 'transparent',
              border: '1px solid var(--admin-border)',
              borderRadius: 6,
              cursor: isBusy ? 'wait' : 'pointer',
              color: 'var(--admin-slate)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
            }}
          >
            <RefreshCw size={13} className={state === 'drafting' ? 'animate-spin' : ''} />
            Regenerate
          </button>
        </div>

        {state === 'drafting' && (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--admin-muted)' }} data-testid="followup-loading">
            <Loader2 size={28} className="animate-spin" style={{ display: 'inline-block' }} />
            <div style={{ marginTop: 8, fontSize: 13 }}>Drafting your follow-up…</div>
          </div>
        )}

        {state !== 'drafting' && draft && (
          <>
            {flags.length > 0 && (
              <div
                data-testid="followup-compliance-banner"
                style={{
                  display: 'flex',
                  gap: 10,
                  background: '#FEF3C7',
                  border: '1px solid #D97706',
                  color: '#78350F',
                  padding: 10,
                  borderRadius: 8,
                  fontSize: 12,
                  marginBottom: 12,
                }}
              >
                <AlertTriangle size={16} style={{ flexShrink: 0, color: '#D97706' }} />
                <div>
                  <strong>Compliance flags detected</strong> — review before sending:
                  <ul style={{ margin: '4px 0 0 18px' }}>
                    {flags.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                </div>
              </div>
            )}

            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--admin-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="followup-subject"
              style={{
                width: '100%',
                padding: '10px 12px',
                marginBottom: 12,
                border: '1px solid var(--admin-border)',
                borderRadius: 6,
                fontSize: 14,
                background: 'var(--admin-surface)',
                color: 'var(--admin-slate)',
              }}
            />
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--admin-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={14}
              data-testid="followup-body"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--admin-border)',
                borderRadius: 6,
                fontSize: 13,
                lineHeight: 1.5,
                fontFamily: 'inherit',
                background: 'var(--admin-surface)',
                color: 'var(--admin-slate)',
                resize: 'vertical',
              }}
            />
            {draft.model && (
              <div style={{ fontSize: 11, color: 'var(--admin-muted)', marginTop: 4 }}>
                Model: <code>{draft.model}</code> · Tokens est: {draft.tokens_estimated}
              </div>
            )}

            {error && (
              <div style={{ color: '#991B1B', fontSize: 12, marginTop: 8 }} data-testid="followup-error">
                {error}
              </div>
            )}

            {state === 'sent' && (
              <div style={{ color: '#065F46', fontSize: 13, marginTop: 8, padding: 8, background: '#D1FAE5', borderRadius: 6 }} data-testid="followup-sent">
                ✓ Sent and logged to the lead's activity trail.
              </div>
            )}
            {state === 'saved' && (
              <div style={{ color: '#065F46', fontSize: 13, marginTop: 8, padding: 8, background: '#D1FAE5', borderRadius: 6 }} data-testid="followup-saved">
                ✓ Saved to activity log (not emailed).
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                type="button"
                onClick={() => submit(true)}
                disabled={isBusy || !subject || !body}
                data-testid="followup-save"
                style={{
                  padding: '8px 14px',
                  background: 'transparent',
                  border: '1px solid var(--admin-navy-solid)',
                  borderRadius: 6,
                  color: 'var(--admin-navy)',
                  cursor: isBusy ? 'wait' : 'pointer',
                  fontSize: 13,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  opacity: isBusy || !subject || !body ? 0.6 : 1,
                }}
              >
                <Save size={14} /> Save as activity
              </button>
              <button
                type="button"
                onClick={() => submit(false)}
                disabled={isBusy || !subject || !body}
                data-testid="followup-send"
                style={{
                  padding: '8px 14px',
                  background: 'var(--admin-gold-solid)',
                  border: 'none',
                  borderRadius: 6,
                  color: '#FFFFFF',
                  cursor: isBusy ? 'wait' : 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  opacity: isBusy || !subject || !body ? 0.6 : 1,
                }}
              >
                {state === 'sending' ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Send
              </button>
            </div>
          </>
        )}

        {state === 'error' && !draft && (
          <div style={{ color: '#991B1B', fontSize: 13, padding: '20px 0' }} data-testid="followup-error">
            {error}
            <button
              type="button"
              onClick={() => generate(tone)}
              style={{ marginLeft: 12, padding: '4px 10px', border: '1px solid var(--admin-border)', borderRadius: 4, cursor: 'pointer', background: 'transparent', color: 'var(--admin-slate)' }}
            >
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
